/**
 * `/invite/:token` (spec M0-F3 §8.5).
 *
 * Shows the account name, the inviter and the invited address read-only, then Full name,
 * Password and Confirm password. An expired, revoked or already-accepted token says only
 * "This invitation is no longer valid." — no further detail (acceptance C5).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { AuthError, AuthShell } from './AuthShell';

export function AcceptInvitation() {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const { token = '' } = useParams();

  const invitation = useQuery(trpc.auth.invitation.describe.queryOptions({ token }));
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: (input: { token: string; name: string; password: string }) =>
      client.auth.invitation.accept.mutate(input),
    onSuccess: () => window.location.assign('/'),
    onError: (caught: unknown) => setError((caught as { message?: string }).message ?? 'Could not join.'),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (accept.isPending) return;
    if (password !== confirmation) {
      setError('The two passwords do not match.');
      return;
    }
    setError(null);
    accept.mutate({ token, name: name.trim(), password });
  };

  if (invitation.isPending) {
    return (
      <AuthShell title="Join" testId="invite-page">
        <p className="ez-auth__hint">Loading the invitation…</p>
      </AuthShell>
    );
  }

  const data = invitation.data;
  if (!data || data.expired) {
    return (
      <AuthShell title="Invitation" testId="invite-page">
        <p className="ez-auth__panel" data-testid="invite-invalid">
          This invitation is no longer valid.
        </p>
        <p className="ez-auth__links">
          <a className="ez-auth__link" href="/login">
            ← Back to log in
          </a>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Join ${data.accountName}`}
      subtitle={data.inviterName ? `${data.inviterName} invited you` : undefined}
      testId="invite-page"
    >
      <form className="ez-auth__form" onSubmit={submit} noValidate>
        <div>
          <label className="ez-auth__label" htmlFor="invite-email">
            Your email address
          </label>
          <input
            id="invite-email"
            className="ez-auth__input"
            type="email"
            value={data.email}
            readOnly
            data-testid="invite-email"
          />
        </div>
        <div>
          <label className="ez-auth__label" htmlFor="invite-name">
            Full name
          </label>
          <input
            id="invite-name"
            className="ez-auth__input"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            data-testid="invite-name"
          />
        </div>
        <div>
          <label className="ez-auth__label" htmlFor="invite-password">
            Password
          </label>
          <input
            id="invite-password"
            className="ez-auth__input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            data-testid="invite-password"
          />
          <p className="ez-auth__hint">At least 12 characters. Avoid your email address.</p>
        </div>
        <div>
          <label className="ez-auth__label" htmlFor="invite-confirm">
            Confirm password
          </label>
          <input
            id="invite-confirm"
            className="ez-auth__input"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            data-testid="invite-confirm"
          />
        </div>
        <AuthError>{error}</AuthError>
        <button
          type="submit"
          className="ez-auth__button"
          disabled={accept.isPending || name.trim() === '' || password === ''}
          data-testid="invite-submit"
        >
          Join Eli Z Monday
        </button>
      </form>
    </AuthShell>
  );
}
